import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    { value: '', label: t('contact.selectService') },
    { value: 'visa-schengen', label: t('contact.services.visaSchengen') },
    { value: 'visa-canada', label: t('contact.services.visaCanada') },
    { value: 'gestion-immobiliere', label: t('contact.services.gestionImmo') },
    { value: 'maitrise-oeuvre', label: t('contact.services.maitriseOeuvre') },
    { value: 'strategie-commerciale', label: t('contact.services.strategie') },
    { value: 'formation-equipes', label: t('contact.services.formation') },
    { value: 'hygiene-industrielle', label: t('contact.services.hygiene') },
    { value: 'client-mystere', label: t('contact.services.clientMystere') },
    { value: 'marketing-digital', label: t('contact.services.marketing') },
    { value: 'autre', label: t('contact.services.other') }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success(t('contact.success'), {
          description: t('contact.successText')
        });
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur', {
        description: 'Veuillez réessayer plus tard.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero Header */}
      <section 
        className="page-header"
        style={{ 
          backgroundImage: 'url(https://images.pexels.com/photos/30869731/pexels-photo-30869731.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="contact-header"
      >
        <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans animate-fade-in">{t('nav.contact')}</p>
        <h1 className="font-serif text-4xl md:text-6xl text-white mb-6 animate-fade-in-delay-1">
          {t('contact.title')}
        </h1>
        <div className="gold-line gold-line-center mb-6 animate-fade-in-delay-2"></div>
        <p className="text-white/80 text-lg max-w-2xl text-center animate-fade-in-delay-3">
          {t('contact.subtitle')}
        </p>
      </section>

      {/* Contact Section */}
      <section className="section-luxury bg-white" data-testid="contact-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('contact.info')}</p>
              <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A1A] mb-6">
                {t('contact.stayInTouch')}
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="text-gray-600 mb-8">
                {t('contact.infoText')}
              </p>

              <div className="space-y-6">
                <a 
                  href="tel:+21696050699" 
                  className="flex items-start gap-4 group"
                  data-testid="contact-info-phone"
                >
                  <div className="w-12 h-12 rounded-full border border-[#C9A227] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227] transition-all duration-300">
                    <Phone className="w-5 h-5 text-[#C9A227] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-serif text-lg text-[#1A1A1A] mb-1">{t('contact.phone')}</p>
                    <p className="text-gray-600 group-hover:text-[#C9A227] transition-colors">+216 96 050 699</p>
                    <p className="text-gray-500 text-sm mt-2 italic">{t('contact.whatsappNote')}</p>
                  </div>
                </a>

                <a 
                  href="mailto:commercial@excelsium.online" 
                  className="flex items-start gap-4 group"
                  data-testid="contact-info-email"
                >
                  <div className="w-12 h-12 rounded-full border border-[#C9A227] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227] transition-all duration-300">
                    <Mail className="w-5 h-5 text-[#C9A227] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-serif text-lg text-[#1A1A1A] mb-1">{t('contact.email')}</p>
                    <p className="text-gray-600 group-hover:text-[#C9A227] transition-colors">commercial@excelsium.online</p>
                  </div>
                </a>

                <div className="flex items-start gap-4" data-testid="contact-info-address">
                  <div className="w-12 h-12 rounded-full border border-[#C9A227] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#C9A227]" />
                  </div>
                  <div>
                    <p className="font-serif text-lg text-[#1A1A1A] mb-1">{t('contact.address')}</p>
                    <p className="text-gray-600">Excelsium Conseils<br />Pacha Centre - Montplaisir<br />Tunis</p>
                  </div>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="mt-12 p-6 bg-[#FDFCF8] border border-[#F5EFD3]">
                <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">{t('contact.hours')}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('contact.weekdays')}</span>
                    <span className="text-[#1A1A1A]">10h00 - 19h00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('contact.saturday')}</span>
                    <span className="text-[#1A1A1A]">{t('contact.byAppointment')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('contact.sunday')}</span>
                    <span className="text-[#1A1A1A]">{t('contact.closed')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="contact-form-container p-8 md:p-12">
                <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('contact.form')}</p>
                <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A1A] mb-6">
                  {t('contact.sendMessage')}
                </h2>
                <div className="gold-line mb-8"></div>

                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-20 h-20 rounded-full bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-[#C9A227]" />
                    </div>
                    <h3 className="font-serif text-2xl text-[#1A1A1A] mb-4">{t('contact.success')}</h3>
                    <p className="text-gray-600 mb-8">
                      {t('contact.successText')}
                    </p>
                    <button 
                      onClick={() => setIsSubmitted(false)}
                      className="btn-secondary"
                      data-testid="send-another-message"
                    >
                      {t('contact.sendAnother')}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.firstName')} *
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="input-luxury"
                          placeholder={t('contact.yourFirstName')}
                          data-testid="input-firstname"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.lastName')} *
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className="input-luxury"
                          placeholder={t('contact.yourLastName')}
                          data-testid="input-lastname"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.email')} *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="input-luxury"
                          placeholder={t('contact.yourEmail')}
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.phone')}
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="input-luxury"
                          placeholder={t('contact.phonePlaceholder')}
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.service')} *
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="input-luxury cursor-pointer"
                        data-testid="select-service"
                      >
                        {services.map((service) => (
                          <option key={service.value} value={service.value}>
                            {service.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.message')} *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="input-luxury resize-none"
                        placeholder={t('contact.messagePlaceholder')}
                        data-testid="input-message"
                      />
                    </div>

                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary w-full md:w-auto flex items-center justify-center gap-2"
                        data-testid="submit-button"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('contact.sending')}
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            {t('contact.send')}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
